import React, { useEffect, useState } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { celebrate } from "../../components/CelebrationHub";
import { downloadRowsAsXlsx } from "../../utils/excelDownload";

const employeeSpentForTrip = (trip) => Number(trip?.employee_expenses_total ?? trip?.total_expenses ?? 0);

const CATEGORY_ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌" };
const COMPANY_LABELS = { ebek: "Ebek", ebekmed: "Ebek Med" };
const COMPANY_COLORS = { ebek: { bg:"#FEE2E2", color:"#E31E24" }, ebekmed: { bg:"#CCFBF1", color:"#2AB5A5" } };

function approvalStage(expense, stage) {
  if (!expense.cash_bill_approval_id) return "Not required / not raised";
  const value = stage === "manager" ? expense.cash_bill_manager_status : expense.cash_bill_finance_status;
  if (value === "approved") return "Approved";
  if (value === "declined") return "Declined";
  if (value === "pending") return "Pending";
  return "Not recorded";
}

function overallApproval(expense) {
  if (!expense.cash_bill_approval_id) return "Not required / not raised";
  if (expense.cash_bill_manager_status === "declined" || expense.cash_bill_finance_status === "declined") return "Declined";
  if (expense.cash_bill_finance_status === "approved") return "Fully approved";
  if (expense.cash_bill_manager_status === "approved") return "Manager approved; Finance pending";
  return "Manager approval pending";
}

// Export expenses to a genuine Excel workbook.
function exportToExcel(trip, expenses) {
  const companyName = COMPANY_LABELS[trip.employee_company] || trip.employee_company || "";
  const startDate = new Date(trip.start_date).toLocaleDateString("en-IN");
  const endDate   = trip.end_date ? new Date(trip.end_date).toLocaleDateString("en-IN") : "";
  const total     = expenses.reduce((s, e) => s + parseFloat(e.amount), 0);
  const advance   = parseFloat(trip.advance_amount || 0);
  const balance   = advance - total;

  const rows = expenses.map((e, i) => [
      i + 1,
      new Date(e.expense_date).toLocaleDateString("en-IN"),
      e.category,
      e.description,
      e.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill",
      approvalStage(e, "manager"),
      approvalStage(e, "finance"),
      overallApproval(e),
      Number(e.amount || 0),
    ]);
  downloadRowsAsXlsx({
    filename: `${trip.title.replace(/\s+/g, "_")}_expenses.xlsx`,
    sheetName: "Trip Expenses",
    title: "EBEK EXPENSE REPORT",
    subtitleRows: [`Company: ${companyName}`, `Employee: ${trip.employee_name || ""}`, `Trip: ${trip.title}`,
      `Purpose: ${trip.purpose || ""}`, `Destination: ${trip.destination || ""}`, `Period: ${startDate} – ${endDate}`,
      `Status: ${trip.status}`, `Advance issued: ₹${advance.toFixed(2)}; ${balance >= 0 ? "refund due" : "amount due"}: ₹${Math.abs(balance).toFixed(2)}`],
    headers: ["#", "Date", "Category", "Description", "Bill Type", "Manager Approval", "Finance Approval", "Overall Approval", "Amount (₹)"],
    rows,
    totalRow: ["", "", "", "", "", "", "", "TOTAL EXPENSES", total],
  });
}

const STATUS_COLORS = {
  pending:  { bg:"#FEF3C7", color:"#92400e", label:"Pending with Manager" },
  approved: { bg:"#DBEAFE", color:"#1E40AF", label:"Pending with Finance" },
  declined: { bg:"#FEE2E2", color:"#991B1B", label:"Declined" },
  credited: { bg:"#D1FAE5", color:"#065F46", label:"Credited" },
};

function fmtHours(hours) {
  if (hours == null || isNaN(hours)) return "—";
  const h = Math.round(hours);
  if (h < 1) return "<1h";
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  const rem = h % 24;
  return rem > 0 ? `${d}d ${rem}h` : `${d}d`;
}

function fmtDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-IN", {
    day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit"
  });
}

export default function AdminAdvances() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [trips, setTrips]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState("advance-requests");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripExpenses, setTripExpenses] = useState([]);
  const [loadingExp, setLoadingExp] = useState(false);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);
  const [advanceReturns, setAdvanceReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [updatingReturnId, setUpdatingReturnId] = useState(null);

  // Advance requests tab
  const [advReqs, setAdvReqs]           = useState([]);
  const [loadingAdvReqs, setLoadingAdvReqs] = useState(false);
  const [advReqFilter, setAdvReqFilter]   = useState("approved");
  const [creditingId, setCreditingId]     = useState(null);
  const [creditBankRef, setCreditBankRef] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [creditAdjustmentReason, setCreditAdjustmentReason] = useState("");
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditReq, setCreditReq]         = useState(null);

  // Modals
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showSettleModal,  setShowSettleModal]  = useState(false);
  const [advanceTrip,  setAdvanceTrip]  = useState(null);
  const [settleTrip,   setSettleTrip]   = useState(null);
  const [advanceAmount,    setAdvanceAmount]    = useState("");
  const [advanceBankRef,   setAdvanceBankRef]   = useState("");
  const [advanceNotes,     setAdvanceNotes]     = useState("");
  const [advanceCarryForward, setAdvanceCarryForward] = useState(0);
  const [carryForwardToApply, setCarryForwardToApply] = useState("");
  const [settleNote,            setSettleNote]            = useState("");
  const [settleBankRef,         setSettleBankRef]         = useState("");
  const [settlePaymentDate,     setSettlePaymentDate]     = useState("");
  const [settlePaymentAmount,   setSettlePaymentAmount]   = useState("");
  const [settleBankTransferRef, setSettleBankTransferRef] = useState("");
  const [settleOverspendResolution, setSettleOverspendResolution] = useState("reimbursement");
  const [saving, setSaving] = useState(false);
  const [auditFlags, setAuditFlags] = useState(null); // audit check for current settle trip
  const [downloadingBills, setDownloadingBills] = useState(false);

  // Manual reminder
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderTrip,      setReminderTrip]      = useState(null);

  // Reimbursements tab
  const [reimbursements,        setReimbursements]        = useState([]);
  const [loadingReimb,          setLoadingReimb]          = useState(false);
  const [reimbFilter,           setReimbFilter]           = useState("all");
  const [updatingReimbId,       setUpdatingReimbId]       = useState(null);
  const [showReimbModal,        setShowReimbModal]        = useState(false);
  const [reimbTrip,             setReimbTrip]             = useState(null);
  const [reimbModalStatus,      setReimbModalStatus]      = useState("");
  const [reimbModalAmount,      setReimbModalAmount]      = useState("");
  const [reimbModalRef,         setReimbModalRef]         = useState("");
  const [reimbModalDate,        setReimbModalDate]        = useState("");
  const [reimbModalNotes,       setReimbModalNotes]       = useState("");
  const [reminderMessage,   setReminderMessage]   = useState("");
  const [sendingReminder,   setSendingReminder]   = useState(false);

  // Bill correction
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionExp, setCorrectionExp]             = useState(null);
  const [correctionNote, setCorrectionNote]           = useState("");
  const [requestingCorrection, setRequestingCorrection] = useState(false);

  const handleViewBill = async (exp) => {
    {
      try {
        const response = await API.get(`/expenses/${exp.id}/bill-file`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch { toast.error("Failed to load bill"); }
      return;
    }
    // New Google Drive bills: bill_public_id is a Drive file ID (no "/" in it),
    // and bill_url is already the public Drive view link — open it directly.
    if (exp.bill_public_id && !exp.bill_public_id.includes("/")) {
      window.open(exp.bill_url, "_blank");
      return;
    }
    // Legacy Cloudinary bills: bill_public_id contains "/" — need a signed URL.
    const win = window.open("", "_blank");
    if (!win) { toast.error("Allow popups to view bills"); return; }
    win.document.write("<html><body style='margin:0;padding:20px;font-family:sans-serif'><p>Loading bill...</p></body></html>");
    try {
      if (exp.bill_public_id) {
        const r = await API.get("/expenses/signed-bill-url", { params: { public_id: exp.bill_public_id } });
        win.location.href = r.data.url;
      } else {
        win.location.href = exp.bill_url;
      }
    } catch {
      win.close();
      toast.error("Failed to load bill");
    }
  };

  // Load JSZip from CDN once
  const loadJSZip = () => new Promise((resolve, reject) => {
    if (window.JSZip) { resolve(window.JSZip); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    s.onload = () => resolve(window.JSZip);
    s.onerror = () => reject(new Error("Failed to load JSZip"));
    document.head.appendChild(s);
  });

  const downloadBills = async (trip) => {
    setDownloadingBills(true);
    const toastId = "zip-progress";
    try {
      const bills = tripExpenses.filter(e => e.bill_url);
      if (bills.length === 0) {
        toast.error("No bills found for this trip");
        return;
      }
      toast.loading(`Loading ZIP library...`, { id: toastId });
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const safeName = s => (s || "file").replace(/[^a-zA-Z0-9_\-]/g, "_").replace(/_+/g, "_");

      let fetched = 0;
      for (let i = 0; i < bills.length; i++) {
        const exp = bills[i];
        toast.loading(`Downloading bill ${i + 1} of ${bills.length}...`, { id: toastId });
        try {
          const dateStr = exp.expense_date ? String(exp.expense_date).split("T")[0] : "unknown";
          let blob;
          if (true) {
            // New Google Drive bill — proxy through backend (service account auth, sends JWT automatically)
            const r = await API.get(`/expenses/${exp.id}/bill-file`, { responseType: "blob" });
            blob = r.data;
          } else if (exp.bill_public_id) {
            // Legacy Cloudinary bill — get signed URL then fetch
            const sr = await API.get("/expenses/signed-bill-url", { params: { public_id: exp.bill_public_id } });
            const response = await fetch(sr.data.url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            blob = await response.blob();
          } else {
            const response = await fetch(exp.bill_url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            blob = await response.blob();
          }
          const filename = `${String(i + 1).padStart(2, "0")}_${safeName(exp.category)}_${safeName(dateStr)}.jpg`;
          zip.file(filename, blob);
          fetched++;
        } catch (e) {
          console.warn(`Skipping bill ${i + 1}:`, e.message);
        }
      }

      if (fetched === 0) {
        toast.dismiss(toastId);
        toast.error("Could not download any bills. They may be restricted.");
        return;
      }

      toast.loading("Creating ZIP file...", { id: toastId });
      const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
      toast.dismiss(toastId);
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName(trip.employee_name)}_${safeName(trip.title)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fetched} of ${bills.length} bill(s)!`);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Failed to download bills: " + (err.message || "Unknown error"));
    } finally {
      setDownloadingBills(false);
    }
  };

  const load = () => {
    setLoading(true);
    API.get("/trips/team").then(r => { setTrips(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  const loadAdvReqs = () => {
    setLoadingAdvReqs(true);
    API.get("/advance-requests/all")
      .then(r => setAdvReqs(r.data || []))
      .catch(() => setAdvReqs([]))
      .finally(() => setLoadingAdvReqs(false));
  };

  const loadAdvanceReturns = () => {
    setLoadingReturns(true);
    API.get("/advance-returns/review")
      .then(r => setAdvanceReturns(r.data || []))
      .catch(() => setAdvanceReturns([]))
      .finally(() => setLoadingReturns(false));
  };

  useEffect(() => { load(); loadAdvReqs(); loadAdvanceReturns(); }, []);

  const viewReturnProof = async (item) => {
    try {
      const response = await API.get(`/advance-returns/${item.id}/proof`, { responseType:"blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the payment proof"); }
  };

  const handleReturnDecision = async (item, decision) => {
    const financeNote = window.prompt(
      decision === "verified" ? "Verification note (optional)" : "Enter the reason/note for the employee",
      item.finance_note || ""
    );
    if (financeNote === null) return;
    let verifiedAmount;
    if (decision === "verified") {
      const entered = window.prompt("Amount received and verified", String(item.submitted_amount));
      if (entered === null) return;
      verifiedAmount = Number(entered);
      if (!Number.isFinite(verifiedAmount) || verifiedAmount <= 0 || verifiedAmount > Number(item.submitted_amount)) {
        toast.error("Enter a valid verified amount not exceeding the submitted amount");
        return;
      }
    }
    setUpdatingReturnId(item.id);
    try {
      await API.post(`/advance-returns/${item.id}/decision`, {
        decision,
        finance_note: financeNote,
        verified_amount: verifiedAmount,
      });
      toast.success(decision === "verified" ? "Return verified. The trip can now be settled." : `Return marked ${decision}.`);
      await Promise.all([loadAdvanceReturns(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not update the return");
    } finally { setUpdatingReturnId(null); }
  };

  const viewTrip = async (trip) => {
    setSelectedTrip(trip);
    setLoadingExp(true);
    try {
      const r = await API.get(`/expenses/trip/${trip.id}`);
      setTripExpenses(r.data);
    } catch { setTripExpenses([]); }
    finally { setLoadingExp(false); }
  };

  const downloadLatestTripExcel = async (trip) => {
    try {
      const response = await API.get(`/expenses/trip/${trip.id}`);
      const latestExpenses = response.data || [];
      if (selectedTrip?.id === trip.id) setTripExpenses(latestExpenses);
      await exportToExcel(trip, latestExpenses);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not refresh the approval status for download.");
    }
  };

  const openAdvance = (trip, e) => {
    e.stopPropagation();
    setAdvanceTrip(trip);
    setAdvanceAmount("");
    setAdvanceBankRef("");
    setAdvanceNotes("");
    setAdvanceCarryForward(0);
    setCarryForwardToApply("");
    setShowAdvanceModal(true);
    // Fetch carry-forward balance for this employee
    API.get(`/advance-requests/carry-forward/${trip.employee_id}`)
      .then(r => setAdvanceCarryForward(parseFloat(r.data.carry_forward_balance || 0)))
      .catch(() => {});
  };

  const openSettle = async (trip, e) => {
    e.stopPropagation();
    setSettleTrip(trip);
    setSettleNote("");
    setSettleBankRef("");
    setSettlePaymentDate("");
    setSettlePaymentAmount("");
    setSettleBankTransferRef("");
    setSettleOverspendResolution(trip.overspend_resolution || "reimbursement");
    setAuditFlags(null);
    setShowSettleModal(true);
    // Check audit flags for this trip
    try {
      const res = await API.get(`/audit/settlement-check/${trip.id}`);
      setAuditFlags(res.data);
    } catch { /* ignore */ }
  };

  const handleIssueAdvance = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post("/advances", {
        trip_id: advanceTrip.id,
        amount: advanceAmount,
        bank_reference: advanceBankRef,
        notes: advanceNotes,
        carry_forward_used: carryForwardToApply || 0,
      });
      const combinedTopUp = parseFloat(advanceAmount || 0) + parseFloat(carryForwardToApply || 0);
      toast.success(`₹${combinedTopUp.toLocaleString("en-IN")} added to this trip.`);
      setShowAdvanceModal(false);
      if (selectedTrip?.id === advanceTrip.id) {
        const r = await API.get(`/trips/${advanceTrip.id}`);
        setSelectedTrip(r.data);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to issue advance");
    } finally { setSaving(false); }
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const bal = parseFloat(settleTrip.advance_amount||0) - employeeSpentForTrip(settleTrip) - parseFloat(settleTrip.returned_amount||0);
      const paysNow = bal < 0 && settleOverspendResolution === "reimbursement";
      const response = await API.post("/advances/settle", {
        trip_id          : settleTrip.id,
        overspend_resolution: bal < 0 ? settleOverspendResolution : undefined,
        notes            : settleNote,
        bank_reference   : settleBankRef,
        payment_date     : paysNow ? settlePaymentDate     : undefined,
        payment_amount   : paysNow ? settlePaymentAmount   : undefined,
        bank_transfer_ref: paysNow ? settleBankTransferRef : undefined,
      });
      if (response.data?.supplementary_requested) {
        toast.success(response.data.message || "Supplementary advance requested.");
        setShowSettleModal(false);
        setSelectedTrip(null);
        load();
        return;
      }
      toast.success("Trip settled successfully!");
      celebrate("approved", { title: "Claim settled!", message: "Finance review completed successfully" });
      setShowSettleModal(false);
      setSelectedTrip(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to settle trip");
    } finally { setSaving(false); }
  };

  const renderOverspendResolution = (balance) => balance < 0 && (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:13, fontWeight:800, color:"#1f2937", marginBottom:8 }}>
        How should the excess expense be settled?
      </div>
      <div style={{ display:"grid", gap:8 }}>
        {[
          ["reimbursement", "Pay employee now", "Record the reimbursement payment and close this trip."],
          ["next_advance", "Carry excess into the next advance", "Close this trip and add the company liability to the employee's next bank credit."],
          ["supplementary_advance", "Additional advance for this trip", "Create an approval request for the excess amount; keep this trip open until it is credited."],
        ].map(([value, title, description]) => (
          <label key={value} style={{ display:"flex", gap:10, padding:"11px 12px", borderRadius:10,
            border:`1px solid ${settleOverspendResolution === value ? "#2563EB" : "#D1D5DB"}`,
            background:settleOverspendResolution === value ? "#EFF6FF" : "#fff", cursor:"pointer" }}>
            <input type="radio" name="settle-overspend-resolution" value={value}
              checked={settleOverspendResolution === value}
              onChange={e => setSettleOverspendResolution(e.target.value)} />
            <span>
              <strong style={{ display:"block", color:"#111827" }}>{title}</strong>
              <span style={{ display:"block", marginTop:2, fontSize:12, color:"#4B5563" }}>{description}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const openReminderModal = (trip, e) => {
    e.stopPropagation();
    setReminderTrip(trip);
    setReminderMessage("");
    setShowReminderModal(true);
  };

  const handleSendReminder = async (e) => {
    e.preventDefault();
    setSendingReminder(true);
    try {
      const res = await API.post(`/trips/${reminderTrip.id}/send-reminder`, { message: reminderMessage || undefined });
      toast.success(`🔔 Reminder #${res.data.reminderNumber} sent to ${reminderTrip.employee_name}!`);
      setShowReminderModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send reminder");
    } finally { setSendingReminder(false); }
  };

  const handleDeleteExpense = async (expense) => {
    if (!isSuperAdmin || selectedTrip.status === "settled" || deletingExpenseId) return;
    const input = window.prompt(`Reason for deleting ${expense.category} expense of ₹${expense.amount} (required). The employee will be notified and the reason saved in trip comments.`);
    if (input === null) return;
    const reason = input.trim();
    if (!reason || reason.length > 1000) return toast.error("Enter a reason between 1 and 1000 characters");
    if (!window.confirm("Delete this expense and update the trip totals?")) return;
    setDeletingExpenseId(expense.id);
    try {
      await API.delete(`/expenses/${expense.id}`, { data: { reason } });
      toast.success("Expense deleted — employee notified");
      setTripExpenses(current => current.filter(item => item.id !== expense.id));
      try {
        const [tripRes, expRes] = await Promise.all([
          API.get(`/trips/${selectedTrip.id}`), API.get(`/expenses/trip/${selectedTrip.id}`),
        ]);
        setSelectedTrip(tripRes.data);
        setTripExpenses(expRes.data);
      } catch {
        toast.error("Expense was deleted. Reopen the trip to refresh its totals.");
        setSelectedTrip(null);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete expense");
    } finally { setDeletingExpenseId(null); }
  };

  const openCorrectionModal = (exp, e) => {
    e.stopPropagation();
    setCorrectionExp(exp);
    setCorrectionNote(exp.bill_correction_note || "");
    setShowCorrectionModal(true);
  };

  const handleRequestCorrection = async (e) => {
    e.preventDefault();
    setRequestingCorrection(true);
    try {
      await API.post(`/expenses/${correctionExp.id}/request-correction`, { note: correctionNote });
      toast.success("Correction requested — employee has been notified by email");
      setShowCorrectionModal(false);
      // Refresh expenses + trip
      const [tripRes, expRes] = await Promise.all([
        API.get(`/trips/${selectedTrip.id}`),
        API.get(`/expenses/trip/${selectedTrip.id}`)
      ]);
      setSelectedTrip(tripRes.data);
      setTripExpenses(expRes.data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to request correction");
    } finally { setRequestingCorrection(false); }
  };

  const filtered  = trips.filter(t => tab === "all" ? true : t.status === tab);
  const submitted = trips.filter(t => t.status === "submitted");
  const pendingAdvReqs = advReqs.filter(r => r.status === "pending").length;
  const approvedAdvReqs = advReqs.filter(r => r.status === "approved").length; // approved but not yet credited

  const loadReimbursements = () => {
    setLoadingReimb(true);
    API.get("/reimbursements/pending")
      .then(r => { setReimbursements(r.data); setLoadingReimb(false); })
      .catch(() => setLoadingReimb(false));
  };

  const handleTabChange = (key) => {
    setTab(key);
    if (key === "advance-requests" && advReqs.length === 0) loadAdvReqs();
    if (key === "advance-returns") loadAdvanceReturns();
    if (key === "reimbursements"   && reimbursements.length === 0) loadReimbursements();
  };

  const openReimbModal = (trip) => {
    setReimbTrip(trip);
    setReimbModalStatus("");
    setReimbModalAmount(trip.reimbursement_amount ? String(trip.reimbursement_amount) : "");
    setReimbModalRef(trip.payment_reference || "");
    setReimbModalDate(trip.reimbursement_date ? trip.reimbursement_date.slice(0,10) : "");
    setReimbModalNotes(trip.reimbursement_notes || "");
    setShowReimbModal(true);
  };

  const handleUpdateReimb = async (e) => {
    e.preventDefault();
    if (!reimbModalStatus) { toast.error("Select a new status"); return; }
    setUpdatingReimbId(reimbTrip.id);
    try {
      await API.patch(`/reimbursements/${reimbTrip.id}/status`, {
        status:               reimbModalStatus,
        reimbursement_amount: reimbModalAmount ? parseFloat(reimbModalAmount) : undefined,
        payment_reference:    reimbModalRef    || undefined,
        reimbursement_date:   reimbModalDate   || undefined,
        reimbursement_notes:  reimbModalNotes  || undefined,
      });
      toast.success(`Status updated to ${reimbModalStatus.replace("_"," ")}`);
      setShowReimbModal(false);
      loadReimbursements();
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    } finally {
      setUpdatingReimbId(null);
    }
  };

  const openCreditModal = (req) => {
    setCreditReq(req);
    setCreditBankRef("");
    setCreditAmount(String(req.approved_amount ?? req.amount ?? ""));
    setCreditAdjustmentReason("");
    setShowCreditModal(true);
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    setCreditingId(creditReq.id);
    try {
      const creditedAmt = parseFloat(creditAmount);
      const response = await API.post(`/advance-requests/${creditReq.id}/credit`, {
        bank_reference: creditBankRef,
        credited_amount: creditedAmt,
        adjustment_reason: creditAdjustmentReason,
      });
      const bankTotal = Number(response.data?.bank_credit_total || creditedAmt);
      const oldLiability = Number(response.data?.previous_company_liability_paid || 0);
      toast.success(oldLiability > 0
        ? `₹${bankTotal.toLocaleString("en-IN")} recorded: ₹${creditedAmt.toLocaleString("en-IN")} usable advance + ₹${oldLiability.toLocaleString("en-IN")} previous company liability.`
        : `₹${creditedAmt.toLocaleString("en-IN")} marked as credited to ${creditReq.employee_name}`);
      setShowCreditModal(false);
      setAdvReqs(prev => prev.map(r => r.id === creditReq.id ? { ...r, status:"credited", credited_amount: creditedAmt, credited_at: new Date().toISOString() } : r));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to mark as credited");
    } finally {
      setCreditingId(null);
    }
  };

  const correctionTrips = trips.filter(t => t.status === "correction");
  const pendingReimb = reimbursements.filter(r => r.reimbursement_status === "submitted" || r.reimbursement_status === "under_review").length;
  const pendingReturns = advanceReturns.filter(r => r.status === "pending" || r.status === "queried").length;
  const TABS = [
    { key:"advance-requests", label:"💸 Advance Requests", count: pendingAdvReqs + approvedAdvReqs, urgent: pendingAdvReqs > 0 || approvedAdvReqs > 0 },
    { key:"advance-returns",  label:"↩️ Returned Funds", count: pendingReturns, urgent: pendingReturns > 0 },
    { key:"submitted",        label:"Awaiting Settlement", count: trips.filter(t=>t.status==="submitted").length },
    { key:"correction",       label:"Bill Correction",     count: correctionTrips.length, urgent: correctionTrips.length > 0 },
    { key:"active",           label:"Active Trips",        count: trips.filter(t=>t.status==="active").length },
    { key:"settled",          label:"Settled",             count: trips.filter(t=>t.status==="settled").length },
    { key:"all",              label:"All Trips",           count: trips.length },
    { key:"reimbursements",   label:"Reimbursements",      count: pendingReimb, urgent: pendingReimb > 0 },
  ];

  const accentColor = isSuperAdmin ? "#7C3AED" : "#E31E24";

  // ── Trip Detail View ──
  if (selectedTrip) {
    const balance = parseFloat(selectedTrip.advance_amount||0) - employeeSpentForTrip(selectedTrip) - parseFloat(selectedTrip.returned_amount||0);
    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24, flexWrap:"wrap" }}>
          <button onClick={() => setSelectedTrip(null)} className="btn btn-secondary btn-sm">← Back</button>
          <div style={{ flex:1 }}>
            <h1 className="page-title" style={{ marginBottom:2 }}>{selectedTrip.title}</h1>
            <p className="page-subtitle" style={{ margin:0 }}>
              👤 {selectedTrip.employee_name}
              {isSuperAdmin && selectedTrip.employee_company && (
                <span style={{ marginLeft:8, padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:700,
                  background: COMPANY_COLORS[selectedTrip.employee_company]?.bg || "#f3f4f6",
                  color: COMPANY_COLORS[selectedTrip.employee_company]?.color || "#374151" }}>
                  {COMPANY_LABELS[selectedTrip.employee_company] || selectedTrip.employee_company}
                </span>
              )}
              {selectedTrip.purpose && ` • ${selectedTrip.purpose}`}
              {selectedTrip.destination && ` • 📍${selectedTrip.destination}`}
            </p>
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => downloadLatestTripExcel(selectedTrip)}
              disabled={tripExpenses.length === 0}
              title="Download as Excel"
            >
              📊 Export Excel
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => downloadBills(selectedTrip)}
              disabled={downloadingBills || tripExpenses.filter(e => e.bill_url).length === 0}
              title="Download all bill photos as ZIP"
            >
              {downloadingBills ? "⏳ Downloading..." : "📎 Download Bills"}
            </button>
            {selectedTrip.status === "active" && (
              <button className="btn btn-primary" onClick={(e) => openAdvance(selectedTrip, e)}>💰 Issue Advance</button>
            )}
            {selectedTrip.status === "submitted" && (
              <button className="btn btn-primary" onClick={(e) => openSettle(selectedTrip, e)}>✅ Settle Trip</button>
            )}
            {(selectedTrip.status === "active" || selectedTrip.status === "correction") && (
              <button
                className="btn btn-secondary"
                onClick={(e) => openReminderModal(selectedTrip, e)}
                style={{ border:"1px solid #3B82F6", color:"#1D4ED8", background:"#EFF6FF" }}>
                🔔 Send Reminder
              </button>
            )}
          </div>
          <span className={`badge badge-${selectedTrip.status === "correction" ? "pending" : selectedTrip.status}`}
            style={{ fontSize:13, padding:"6px 14px",
              ...(selectedTrip.status === "correction" ? { background:"#FEF3C7", color:"#92400e" } : {}) }}>
            {selectedTrip.status === "correction" ? "⚠️ Correction" : selectedTrip.status}
          </span>
        </div>

        {/* Summary cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:16, marginBottom:24 }}>
          {[
            { label:"Advance Issued", value:`₹${parseFloat(selectedTrip.advance_amount||0).toLocaleString("en-IN")}`, color:"#d97706", bg:"#FEF3C7", icon:"💰" },
            { label:"Total Spent",    value:`₹${parseFloat(selectedTrip.total_expenses||0).toLocaleString("en-IN")}`,  color:"#E31E24", bg:"#FEE2E2", icon:"💸" },
            { label:"Returned", value:`₹${parseFloat(selectedTrip.returned_amount||0).toLocaleString("en-IN")}`, color:"#0369A1", bg:"#E0F2FE", icon:"↩️" },
            { label:balance>=0?"Refund Due":"Pay Extra", value:`₹${Math.abs(balance).toLocaleString("en-IN")}`, color:balance>=0?"#059669":"#DC2626", bg:balance>=0?"#D1FAE5":"#FEE2E2", icon:balance>=0?"↩️":"➕" },
            { label:"Expense Items",  value:tripExpenses.length, color:"#4f46e5", bg:"#EEF2FF", icon:"📋" },
          ].map(s => (
            <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"16px 20px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:s.color, opacity:0.7, textTransform:"uppercase" }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:s.color, marginTop:4 }}>{s.icon} {s.value}</div>
            </div>
          ))}
        </div>

        {/* Bank details if available */}
        {(selectedTrip.bank_account || selectedTrip.bank_name) && (
          <div style={{ padding:"14px 20px", background:"#F0FDF4", borderRadius:12, marginBottom:20, border:"1px solid #86EFAC", display:"flex", gap:24, flexWrap:"wrap" }}>
            <div><div style={{ fontSize:11, color:"#166534", fontWeight:700 }}>BANK</div><div style={{ fontWeight:700, color:"#15803d" }}>{selectedTrip.bank_name || "—"}</div></div>
            <div><div style={{ fontSize:11, color:"#166534", fontWeight:700 }}>ACCOUNT</div><div style={{ fontWeight:700, color:"#15803d" }}>{selectedTrip.bank_account || "—"}</div></div>
            <div><div style={{ fontSize:11, color:"#166534", fontWeight:700 }}>IFSC</div><div style={{ fontWeight:700, color:"#15803d" }}>{selectedTrip.ifsc_code || "—"}</div></div>
          </div>
        )}

        {/* ── Request Bill Correction Modal (inside detail view) ── */}
        {showCorrectionModal && correctionExp && (
          <div className="modal-overlay" onClick={() => setShowCorrectionModal(false)}>
            <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize:18, fontWeight:700 }}>⚠️ Request Bill Correction</h2>
                <button onClick={() => setShowCorrectionModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
              </div>
              <form onSubmit={handleRequestCorrection}>
                <div className="modal-body">
                  <div style={{ padding:"14px 16px", background:"#FEF9F0", border:"1px solid #FDE68A", borderRadius:12, marginBottom:18 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#92400e", textTransform:"uppercase", marginBottom:6 }}>Flagging this expense</div>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:22 }}>{CATEGORY_ICONS[correctionExp.category]||"📌"}</span>
                      <div>
                        <div style={{ fontWeight:700, color:"#1f2937" }}>{correctionExp.category} — {correctionExp.description}</div>
                        <div style={{ fontSize:13, color:"#6b7280" }}>
                          ₹{parseFloat(correctionExp.amount).toLocaleString("en-IN")} •
                          📅 {new Date(correctionExp.expense_date).toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding:"12px 16px", background:"#FFFBEB", borderRadius:10, marginBottom:16, fontSize:13, color:"#78350f", border:"1px solid #FDE68A" }}>
                    <strong>What happens:</strong> The trip status will change to "Correction" and the employee will receive an email notification with your note, asking them to reupload the correct bill.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correction Note for Employee *</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="e.g. Bill is blurry, please reupload a clearer photo. Or: Bill amount doesn't match the claimed amount."
                      value={correctionNote}
                      onChange={e => setCorrectionNote(e.target.value)}
                      required
                      autoFocus
                      style={{ resize:"vertical", minHeight:80 }}
                    />
                    <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>This note will appear in the employee's email and on their trip page.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCorrectionModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={requestingCorrection}
                    style={{ background:"#D97706" }}>
                    {requestingCorrection ? "Sending..." : "⚠️ Request Correction"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Settle Modal (inside detail view) ── */}
        {showSettleModal && settleTrip && (() => {
          const bal = parseFloat(settleTrip.advance_amount||0) - employeeSpentForTrip(settleTrip) - parseFloat(settleTrip.returned_amount||0);
          return (
            <div className="modal-overlay" onClick={() => setShowSettleModal(false)}>
              <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 style={{ fontSize:18, fontWeight:700 }}>✅ Settle Trip</h2>
                  <button onClick={() => setShowSettleModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
                </div>
                <form onSubmit={handleSettle}>
                  <div className="modal-body">
                    {/* ── Audit Gate Banner ── */}
                    {auditFlags && !auditFlags.can_settle && (
                      <div style={{ padding:"12px 16px", background:"#FEF2F2", border:"2px solid #FCA5A5", borderRadius:10, marginBottom:16 }}>
                        <div style={{ fontWeight:800, color:"#DC2626", fontSize:14, marginBottom:4 }}>
                          🚫 Settlement Blocked — Audit Review Required
                        </div>
                        <div style={{ fontSize:13, color:"#9B1C1C", lineHeight:1.6 }}>
                          This trip has <strong>{auditFlags.open_count} open</strong> and <strong>{auditFlags.escalated_count} escalated</strong> audit flag(s).
                          All flags must be marked as <em>Reviewed</em> or <em>False Positive</em> before settlement can proceed.
                        </div>
                        <a href="/audit" style={{ display:"inline-block", marginTop:8, fontSize:12, fontWeight:700, color:"#DC2626", textDecoration:"underline" }}>
                          → Go to Audit Dashboard to review flags
                        </a>
                      </div>
                    )}
                    {auditFlags && auditFlags.can_settle && (
                      <div style={{ padding:"10px 14px", background:"#F0FDF4", border:"1px solid #86EFAC", borderRadius:10, marginBottom:16, fontSize:13, color:"#166534" }}>
                        ✅ <strong>Audit cleared</strong> — all flags reviewed. Settlement can proceed.
                      </div>
                    )}
                    <div style={{ padding:"14px 16px", background:"#f9fafb", borderRadius:10, marginBottom:16 }}>
                      <div style={{ fontWeight:700, color:"#1f2937" }}>{settleTrip.title}</div>
                      <div style={{ fontSize:13, color:"#6b7280", marginTop:4 }}>👤 {settleTrip.employee_name}</div>
                      {settleTrip.bank_account && (
                        <div style={{ fontSize:12, color:"#059669", marginTop:6, fontWeight:600 }}>
                          🏦 {settleTrip.bank_name} · A/C: {settleTrip.bank_account} · IFSC: {settleTrip.ifsc_code}
                        </div>
                      )}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(105px,1fr))", gap:10, marginBottom:16 }}>
                      {[
                        { label:"Advance Given",  value:`₹${parseFloat(settleTrip.advance_amount||0).toLocaleString("en-IN")}`, color:"#d97706", bg:"#FEF3C7" },
                        { label:"Total Spent",    value:`₹${parseFloat(settleTrip.total_expenses||0).toLocaleString("en-IN")}`, color:"#E31E24", bg:"#FEE2E2" },
                        { label:"Returned", value:`₹${parseFloat(settleTrip.returned_amount||0).toLocaleString("en-IN")}`, color:"#0369A1", bg:"#E0F2FE" },
                        { label:bal>=0?"Carry Forward":"Pay More", value:`₹${Math.abs(bal).toLocaleString("en-IN")}`, color:bal>=0?"#059669":"#DC2626", bg:bal>=0?"#D1FAE5":"#FEE2E2" },
                      ].map(s => (
                        <div key={s.label} style={{ background:s.bg, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                          <div style={{ fontSize:10, fontWeight:700, color:s.color, opacity:0.7, textTransform:"uppercase" }}>{s.label}</div>
                          <div style={{ fontSize:16, fontWeight:800, color:s.color, marginTop:4 }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    {bal > 0 && (
                      <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#D1FAE5", border:"1px solid #6EE7B7" }}>
                        <div style={{ fontWeight:700, color:"#065F46", fontSize:14 }}>✅ Balance of ₹{Math.abs(bal).toLocaleString("en-IN")} kept in employee's account</div>
                        <div style={{ fontSize:12, color:"#047857", marginTop:4 }}>This surplus stays with the employee and will be deducted from their next advance — no collection needed.</div>
                      </div>
                    )}
                    {renderOverspendResolution(bal)}
                    {bal < 0 && settleOverspendResolution === "next_advance" && (
                      <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#EFF6FF", border:"1px solid #93C5FD" }}>
                        <div style={{ fontWeight:700, color:"#1E3A8A", fontSize:14 }}>↪ Carry ₹{Math.abs(bal).toLocaleString("en-IN")} into the next advance</div>
                        <div style={{ fontSize:12, color:"#1D4ED8", marginTop:4 }}>This closes the current trip. The amount remains company money owed to the employee and will be paid in addition to the next trip's usable advance.</div>
                      </div>
                    )}
                    {bal < 0 && settleOverspendResolution === "reimbursement" && (
                      <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#FEE2E2", border:"1px solid #FCA5A5" }}>
                        <div style={{ fontWeight:700, color:"#991b1b", fontSize:14 }}>➕ Pay ₹{Math.abs(bal).toLocaleString("en-IN")} to employee</div>
                        <div style={{ fontSize:12, color:"#b91c1c", marginTop:4 }}>Employee spent more than the advance. Payment details below are required.</div>
                      </div>
                    )}
                    {bal === 0 && (
                      <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#F3F4F6", border:"1px solid #E5E7EB" }}>
                        <div style={{ fontWeight:700, color:"#374151" }}>✅ Perfectly balanced — no payment needed</div>
                      </div>
                    )}
                    {bal < 0 && settleOverspendResolution === "reimbursement" && (
                      <div style={{ border:"1px solid #FCA5A5", borderRadius:10, padding:"14px 16px", marginBottom:16, background:"#FFF5F5" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#991b1b", marginBottom:12 }}>💸 Payment Details (required)</div>
                        <div className="form-group" style={{ marginBottom:12 }}>
                          <label className="form-label">Payment Date *</label>
                          <input type="date" className="form-control" required value={settlePaymentDate} onChange={e => setSettlePaymentDate(e.target.value)} />
                        </div>
                        <div className="form-group" style={{ marginBottom:12 }}>
                          <label className="form-label">Amount Paid (₹) *</label>
                          <input type="number" className="form-control" placeholder={Math.abs(bal).toFixed(2)} min="0" step="0.01" required value={settlePaymentAmount} onChange={e => setSettlePaymentAmount(e.target.value)} />
                        </div>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          <label className="form-label">Bank Transfer / UPI Reference</label>
                          <input type="text" className="form-control" placeholder="e.g. UTR/UPI ref number" value={settleBankTransferRef} onChange={e => setSettleBankTransferRef(e.target.value)} />
                        </div>
                      </div>
                    )}
                    {bal < 0 && settleOverspendResolution === "reimbursement" && (
                    <div className="form-group">
                      <label className="form-label">Bank / UPI Reference (payment to employee)</label>
                      <input className="form-control" placeholder="e.g. NEFT/UPI transaction ref" value={settleBankRef} onChange={e => setSettleBankRef(e.target.value)} />
                    </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Settlement Notes (optional)</label>
                      <input className="form-control" placeholder="e.g. Refund received via UPI on 01-May-2026" value={settleNote} onChange={e => setSettleNote(e.target.value)} />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowSettleModal(false)}>Cancel</button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving || (auditFlags && !auditFlags.can_settle)}
                      title={auditFlags && !auditFlags.can_settle ? "Cannot settle — open audit flags must be reviewed first" : ""}
                    >
                      {saving ? "Saving..." : (auditFlags && !auditFlags.can_settle) ? "🚫 Blocked by Audit"
                        : settleOverspendResolution === "supplementary_advance" ? "Request Additional Advance"
                          : "✅ Mark as Settled"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}

        {/* ── Send Reminder Modal (inside detail view) ── */}
        {showReminderModal && reminderTrip && (
          <div className="modal-overlay" onClick={() => setShowReminderModal(false)}>
            <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize:18, fontWeight:700 }}>🔔 Send Reminder</h2>
                <button onClick={() => setShowReminderModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
              </div>
              <form onSubmit={handleSendReminder}>
                <div className="modal-body">
                  <div style={{ padding:"14px 16px", background:"#EFF6FF", borderRadius:12, marginBottom:16, border:"1px solid #BFDBFE" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#1e40af", marginBottom:4 }}>Sending reminder to:</div>
                    <div style={{ fontWeight:800, color:"#1f2937" }}>{reminderTrip.employee_name}</div>
                    <div style={{ fontSize:13, color:"#6b7280" }}>Trip: {reminderTrip.title}</div>
                    {reminderTrip.advance_amount > 0 && (
                      <div style={{ fontSize:13, color:"#d97706", fontWeight:600, marginTop:4 }}>
                        Advance: ₹{parseFloat(reminderTrip.advance_amount).toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                  <div style={{ padding:"12px 16px", background:"#F0F9FF", borderRadius:10, marginBottom:16, fontSize:13, color:"#0369a1", border:"1px solid #BAE6FD" }}>
                    <strong>What happens:</strong> Employee will receive an email + push notification reminding them to submit their expense bills.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Custom Message (optional)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="e.g. Please submit your bills at the earliest. Accounts closing this week."
                      value={reminderMessage}
                      onChange={e => setReminderMessage(e.target.value)}
                      style={{ resize:"vertical", minHeight:80 }}
                    />
                    <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Leave blank to send the standard reminder.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowReminderModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={sendingReminder}
                    style={{ background:"#1D4ED8" }}>
                    {sendingReminder ? "Sending..." : "🔔 Send Reminder"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Balance note */}
        {selectedTrip.status === "submitted" && (
          <div style={{ padding:"16px 20px", borderRadius:12, marginBottom:24, background:balance>=0?"#D1FAE5":"#FEE2E2", border:`1px solid ${balance>=0?"#6EE7B7":"#FCA5A5"}` }}>
            <div style={{ fontWeight:700, color:balance>=0?"#065F46":"#991b1b", fontSize:15 }}>
              {balance>=0
                ? `✅ Balance of ₹${Math.abs(balance).toLocaleString("en-IN")} carried forward to next advance`
                : `⚠️ Finance should pay ₹${Math.abs(balance).toLocaleString("en-IN")} extra to Employee`}
            </div>
            <div style={{ fontSize:13, color:balance>=0?"#047857":"#b91c1c", marginTop:4 }}>
              Advance: ₹{parseFloat(selectedTrip.advance_amount||0).toLocaleString("en-IN")} &nbsp;—&nbsp;
              Total Spent: ₹{parseFloat(selectedTrip.total_expenses||0).toLocaleString("en-IN")}
            </div>
          </div>
        )}

        {/* Expenses list */}
        {selectedTrip.notes && (
          <div className="card" style={{ padding:20, marginBottom:20, whiteSpace:"pre-wrap" }}>
            <strong>Trip comments</strong><div>{selectedTrip.notes}</div>
          </div>
        )}
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:"20px 24px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>Expense Entries</h3>
            {tripExpenses.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => downloadLatestTripExcel(selectedTrip)}>
                📥 Download Excel
              </button>
            )}
          </div>
          {loadingExp ? (
            <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Loading...</div>
          ) : tripExpenses.length === 0 ? (
            <div className="empty-state"><h3>No expenses logged yet</h3></div>
          ) : (
            <div>
              {tripExpenses.map(exp => (
                <div key={exp.id} style={{
                  padding:"16px 24px", borderBottom:"1px solid #f9fafb", display:"flex", alignItems:"flex-start", gap:16,
                  background: exp.bill_correction_requested ? "rgba(251,191,36,0.06)" : "transparent"
                }}>
                  <div style={{ fontSize:26, marginTop:2 }}>{CATEGORY_ICONS[exp.category]||"📌"}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontWeight:700, color:"#1f2937" }}>{exp.category}</span>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                        background:exp.bill_type==="cash_voucher"?"#FEF3C7":"#D1FAE5",
                        color:exp.bill_type==="cash_voucher"?"#D97706":"#065F46", fontWeight:600 }}>
                        {exp.bill_type==="cash_voucher"?"Cash Voucher":"Bill"}
                      </span>
                      {exp.bill_correction_requested && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#92400e", fontWeight:700 }}>
                          ⚠️ Correction Requested
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>{exp.description}</div>
                    <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>📅 {new Date(exp.expense_date).toLocaleDateString("en-IN")}</div>
                    {exp.bill_correction_note && (
                      <div style={{ fontSize:12, marginTop:6, padding:"6px 10px", background:"#FEF3C7", borderRadius:6, color:"#92400e", fontStyle:"italic" }}>
                        📝 {exp.bill_correction_note}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:17, fontWeight:800, color:"#E31E24" }}>₹{parseFloat(exp.amount).toLocaleString("en-IN")}</div>
                    {exp.bill_url ? (
                      <button onClick={() => handleViewBill(exp)}
                        style={{ fontSize:12, color:"#2AB5A5", background:"none", border:"none", cursor:"pointer", fontWeight:600, padding:0, display:"block", marginTop:4 }}>
                        🖼️ View Bill
                      </button>
                    ) : (
                      <div style={{ fontSize:12, color:"#f59e0b", marginTop:4, fontWeight:600 }}>⚠️ No bill</div>
                    )}
                    {selectedTrip.status !== "settled" && (
                      <button
                        onClick={(e) => openCorrectionModal(exp, e)}
                        style={{ marginTop:8, fontSize:11, padding:"4px 10px", borderRadius:8, border:"1px solid #D97706",
                          background: exp.bill_correction_requested ? "#FEF3C7" : "transparent",
                          color:"#92400e", cursor:"pointer", fontWeight:700, whiteSpace:"nowrap" }}>
                        {exp.bill_correction_requested ? "✏️ Edit Note" : "⚠️ Request Correction"}
                      </button>
                    )}
                    {isSuperAdmin && selectedTrip.status !== "settled" && (
                      <button className="btn btn-danger btn-sm"
                        style={{ display:"block", marginTop:8, marginLeft:"auto" }}
                        disabled={deletingExpenseId !== null}
                        onClick={() => handleDeleteExpense(exp)}>
                        {deletingExpenseId === exp.id ? "Deleting..." : "🗑️ Delete expense"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ padding:"14px 24px", background:"#f9fafb", display:"flex", justifyContent:"flex-end", alignItems:"center", gap:16 }}>
                <span style={{ fontSize:13, color:"#6b7280", fontWeight:600 }}>Total</span>
                <span style={{ fontSize:20, fontWeight:800, color:"#E31E24" }}>
                  ₹{tripExpenses.reduce((s,e)=>s+parseFloat(e.amount),0).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main List View ──
  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 className="page-title">Advances & Settlements</h1>
        <p className="page-subtitle">
          {isSuperAdmin ? "All companies — issue advances and settle trips" : "Issue advances to employees and settle completed trips"}
        </p>
      </div>

      {submitted.length > 0 && (
        <div style={{ padding:"12px 16px", background:"#FEF3C7", borderRadius:12, marginBottom:20, display:"flex", alignItems:"center", gap:8, border:"1px solid #FDE68A", flexWrap:"wrap", overflow:"hidden" }}>
          <span style={{ fontSize:18, flexShrink:0 }}>🕐</span>
          <span style={{ fontWeight:700, color:"#92400e", fontSize:14, flexShrink:0 }}>
            {submitted.length} trip{submitted.length > 1 ? "s" : ""} awaiting settlement
          </span>
          <span style={{ fontSize:13, color:"#78350f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            — Total: ₹{submitted.reduce((s,t)=>s+parseFloat(t.total_expenses||0),0).toLocaleString("en-IN")}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-row" style={{ display:"flex", gap:2, marginBottom:20, borderBottom:"2px solid #f3f4f6", overflowX:"auto", WebkitOverflowScrolling:"touch", paddingBottom:0 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => handleTabChange(t.key)}
            style={{ padding:"9px 14px", background:"none", border:"none", cursor:"pointer", fontWeight:700, fontSize:13, whiteSpace:"nowrap", flexShrink:0,
              color: tab===t.key ? accentColor : "#6b7280",
              borderBottom: tab===t.key ? `3px solid ${accentColor}` : "3px solid transparent",
              marginBottom:-2 }}>
            {t.label} {t.count > 0 && (
              <span style={{
                background: t.urgent && tab!==t.key ? "#FEF3C7" : tab===t.key ? accentColor : "#e5e7eb",
                color: t.urgent && tab!==t.key ? "#92400e" : tab===t.key ? "white" : "#374151",
                borderRadius:10, padding:"2px 7px", fontSize:11, marginLeft:4
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Advance Requests Tab ── */}
      {tab === "advance-requests" && (
        <div>
          {/* Summary bar */}
          {advReqs.length > 0 && (
            <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:20 }}>
              {[
                { label:"Pending Approval", value: advReqs.filter(r=>r.status==="pending").length,  color:"#92400e", bg:"#FEF3C7", icon:"⏳" },
                { label:"Approved — Awaiting Credit", value: advReqs.filter(r=>r.status==="approved").length, color:"#065F46", bg:"#D1FAE5", icon:"✅" },
                { label:"Credited", value: advReqs.filter(r=>r.status==="credited").length, color:"#1E40AF", bg:"#DBEAFE", icon:"💸" },
                { label:"Declined", value: advReqs.filter(r=>r.status==="declined").length, color:"#991B1B", bg:"#FEE2E2", icon:"❌" },
              ].map(s => (
                <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"12px 18px", display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, color:s.color, textTransform:"uppercase" }}>{s.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filter buttons */}
          <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
            {["all","pending","approved","credited","declined"].map(f => (
              <button key={f} onClick={() => setAdvReqFilter(f)}
                style={{
                  padding:"6px 14px", borderRadius:20, fontSize:13, fontWeight:600, cursor:"pointer", border:"none",
                  background: advReqFilter===f ? accentColor : "#f3f4f6",
                  color: advReqFilter===f ? "white" : "#374151"
                }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== "all" && ` (${advReqs.filter(r=>r.status===f).length})`}
              </button>
            ))}
            <button onClick={loadAdvReqs} style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:20, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid #e5e7eb", background:"white", color:"#6b7280" }}>
              🔄 Refresh
            </button>
          </div>

          {loadingAdvReqs ? (
            <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Loading...</div>
          ) : advReqs.length === 0 ? (
            <div className="card"><div className="empty-state"><div style={{ fontSize:48 }}>🏦</div><h3>No advance requests yet</h3></div></div>
          ) : (
            <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch", width:"100%", borderRadius:12 }}>
              <table style={{ minWidth:700, width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:"#f9fafb" }}>
                    <th style={{ padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" }}>Employee</th>
                    {isSuperAdmin && <th style={{ padding:"10px 12px", textAlign:"left" }}>Co.</th>}
                    <th style={{ padding:"10px 12px", textAlign:"left" }}>Trip</th>
                    <th style={{ padding:"10px 12px", textAlign:"right", whiteSpace:"nowrap" }}>Amount</th>
                    <th style={{ padding:"10px 12px", textAlign:"left" }}>Status</th>
                    <th style={{ padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" }}>📅 Requested</th>
                    <th style={{ padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" }}>✅ Approved</th>
                    <th style={{ padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" }}>💸 Credited</th>
                    <th style={{ padding:"10px 12px", textAlign:"center", whiteSpace:"nowrap" }}>⏱ Approve</th>
                    <th style={{ padding:"10px 12px", textAlign:"center", whiteSpace:"nowrap" }}>⏱ Credit</th>
                    <th style={{ padding:"10px 12px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {advReqs
                    .filter(r => advReqFilter === "all" || r.status === advReqFilter)
                    .map(req => {
                      const sc = STATUS_COLORS[req.status] || {};
                      const approveDelay = req.hours_to_approve;
                      const creditDelay  = req.hours_to_credit;
                      const isSlowApprove = approveDelay > 24;
                      const isSlowCredit  = creditDelay  > 48;

                      return (
                        <tr key={req.id}>
                          <td style={{ padding:"10px 12px" }}>
                            <div style={{ fontWeight:700, color:"#1f2937", fontSize:13 }}>{req.employee_name}</div>
                            <div style={{ fontSize:11, color:"#9ca3af" }}>{req.employee_email}</div>
                            {req.manager_name && <div style={{ fontSize:11, color:"#6b7280" }}>Mgr: {req.manager_name}</div>}
                          </td>
                          {isSuperAdmin && (
                            <td style={{ padding:"10px 12px" }}>
                              <span style={{ padding:"2px 6px", borderRadius:8, fontSize:11, fontWeight:700,
                                background: COMPANY_COLORS[req.company]?.bg || "#f3f4f6",
                                color: COMPANY_COLORS[req.company]?.color || "#374151" }}>
                                {COMPANY_LABELS[req.company] || req.company}
                              </span>
                            </td>
                          )}
                          <td style={{ padding:"10px 12px", maxWidth:180 }}>
                            <div style={{ fontWeight:600, color:"#1f2937", fontSize:13 }}>{req.trip_title}</div>
                            {req.destination && <div style={{ fontSize:11, color:"#6b7280" }}>📍 {req.destination}</div>}
                          </td>
                          <td style={{ padding:"10px 12px", fontWeight:800, fontSize:15, color:accentColor, whiteSpace:"nowrap", textAlign:"right" }}>
                            ₹{parseFloat(req.status === "credited" ? (req.credited_amount ?? req.approved_amount ?? req.amount) : (req.approved_amount ?? req.amount)).toLocaleString("en-IN")}
                            {req.approved_amount != null && parseFloat(req.approved_amount) !== parseFloat(req.amount) && (
                              <div style={{ fontSize:11, fontWeight:400, color:"#9ca3af", textDecoration:"line-through" }}>
                                ₹{parseFloat(req.amount).toLocaleString("en-IN")}
                              </div>
                            )}
                          </td>
                          <td style={{ padding:"10px 12px" }}>
                            <span style={{ padding:"3px 8px", borderRadius:8, fontSize:12, fontWeight:700, background:sc.bg, color:sc.color, whiteSpace:"nowrap" }}>
                              {sc.label || req.status}
                            </span>
                          </td>
                          <td style={{ padding:"10px 12px" }}>
                            {req.requested_at ? <>
                              <div style={{ fontSize:12, color:"#374151", whiteSpace:"nowrap" }}>{new Date(req.requested_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{new Date(req.requested_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
                            </> : "—"}
                          </td>
                          <td style={{ padding:"10px 12px" }}>
                            {req.approved_at ? <>
                              <div style={{ fontSize:12, color:"#374151", whiteSpace:"nowrap" }}>{new Date(req.approved_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{new Date(req.approved_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
                            </> : "—"}
                          </td>
                          <td style={{ padding:"10px 12px" }}>
                            {req.credited_at ? <>
                              <div style={{ fontSize:12, color:"#374151", whiteSpace:"nowrap" }}>{new Date(req.credited_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{new Date(req.credited_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
                            </> : "—"}
                          </td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>
                            {approveDelay != null ? (
                              <span style={{ padding:"3px 8px", borderRadius:8, fontSize:12, fontWeight:700,
                                background: isSlowApprove ? "#FEE2E2" : "#D1FAE5",
                                color: isSlowApprove ? "#DC2626" : "#059669" }}>
                                {fmtHours(approveDelay)}
                              </span>
                            ) : req.status === "pending" ? (
                              <span style={{ padding:"3px 8px", borderRadius:8, fontSize:12, fontWeight:700,
                                background: req.hours_pending > 24 ? "#FEE2E2" : "#FEF3C7",
                                color: req.hours_pending > 24 ? "#DC2626" : "#92400e" }}>
                                {fmtHours(req.hours_pending)} ⏳
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>
                            {creditDelay != null ? (
                              <span style={{ padding:"3px 8px", borderRadius:8, fontSize:12, fontWeight:700,
                                background: isSlowCredit ? "#FEE2E2" : "#D1FAE5",
                                color: isSlowCredit ? "#DC2626" : "#059669" }}>
                                {fmtHours(creditDelay)}
                              </span>
                            ) : req.status === "approved" ? (
                              <span style={{ padding:"3px 8px", borderRadius:8, fontSize:12, fontWeight:700,
                                background: req.hours_pending > 48 ? "#FEE2E2" : "#FEF3C7",
                                color: req.hours_pending > 48 ? "#DC2626" : "#92400e" }}>
                                {fmtHours(req.hours_pending)} ⏳
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ padding:"10px 12px", whiteSpace:"nowrap" }}>
                            {req.status === "approved" && (
                              <button onClick={() => openCreditModal(req)}
                                style={{ background:"#1D4ED8", color:"white", border:"none", borderRadius:8, padding:"7px 12px", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                💸 Mark Credited
                              </button>
                            )}
                            {req.status === "credited" && req.bank_reference && (
                              <div style={{ fontSize:11, color:"#1D4ED8", fontWeight:600 }}>UTR: {req.bank_reference}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Employee advance returns ── */}
      {tab === "advance-returns" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
            <div><h2 style={{ margin:0, fontSize:18 }}>Unused advance returned to Finance</h2><p style={{ margin:"4px 0 0", color:"#6b7280", fontSize:13 }}>Verify the proof. Verified money is deducted from the trip balance during settlement.</p></div>
            <button className="btn btn-secondary btn-sm" onClick={loadAdvanceReturns}>🔄 Refresh</button>
          </div>
          {loadingReturns ? <div style={{ textAlign:"center", padding:50 }}>Loading returned payments...</div> : advanceReturns.length === 0 ? (
            <div className="card"><div className="empty-state"><div style={{ fontSize:44 }}>↩️</div><h3>No advance returns submitted</h3></div></div>
          ) : <div style={{ display:"grid", gap:14 }}>{advanceReturns.map(item => {
            const statusStyle = { pending:["#FEF3C7","#92400E"], verified:["#D1FAE5","#065F46"], queried:["#DBEAFE","#1E40AF"], rejected:["#FEE2E2","#991B1B"] }[item.status] || ["#F3F4F6","#374151"];
            return <div key={item.id} className="card" style={{ padding:"18px 22px" }}><div style={{ display:"flex", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:240 }}><div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}><strong>{item.employee_name}</strong><span style={{ padding:"3px 9px", borderRadius:12, fontSize:11, fontWeight:800, background:statusStyle[0], color:statusStyle[1] }}>{item.status.toUpperCase()}</span></div><div style={{ color:"#4b5563", marginTop:5 }}>{item.trip_title}</div><div style={{ color:"#6b7280", fontSize:12, marginTop:5 }}>{item.payment_method?.replace("_", " ")} · Ref: <strong>{item.payment_reference}</strong> · {new Date(item.payment_date).toLocaleDateString("en-IN")}</div>{item.employee_note && <div style={{ marginTop:8, fontSize:13 }}>Employee: {item.employee_note}</div>}{item.finance_note && <div style={{ marginTop:4, fontSize:13 }}>Finance: {item.finance_note}</div>}</div>
              <div style={{ textAlign:"right", minWidth:180 }}><div style={{ fontSize:22, fontWeight:800, color:"#0369A1" }}>₹{Number(item.submitted_amount||0).toLocaleString("en-IN")}</div>{item.status === "verified" && <div style={{ fontSize:12, color:"#047857" }}>Verified: ₹{Number(item.verified_amount||0).toLocaleString("en-IN")}</div>}<div style={{ display:"flex", gap:7, marginTop:10, justifyContent:"flex-end", flexWrap:"wrap" }}><button className="btn btn-secondary btn-sm" onClick={() => viewReturnProof(item)}>📎 Proof</button>{(item.status === "pending" || item.status === "queried") && <><button className="btn btn-primary btn-sm" disabled={updatingReturnId === item.id} onClick={() => handleReturnDecision(item,"verified")}>✓ Verify</button><button className="btn btn-secondary btn-sm" disabled={updatingReturnId === item.id} onClick={() => handleReturnDecision(item,"queried")}>? Query</button><button className="btn btn-secondary btn-sm" disabled={updatingReturnId === item.id} onClick={() => handleReturnDecision(item,"rejected")} style={{ color:"#B91C1C" }}>Reject</button></>}</div></div>
            </div></div>;
          })}</div>}
        </div>
      )}

      {/* ── Reimbursements Tab ── */}
      {tab === "reimbursements" && (
        <div>
          {loadingReimb ? (
            <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Loading reimbursements...</div>
          ) : (
            <>
              {/* Filter buttons */}
              <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
                {[["all","All"],["submitted","Submitted"],["under_review","Under Review"],["approved","Approved"],["payment_initiated","Payment Sent"]].map(([k,l]) => (
                  <button key={k} onClick={() => setReimbFilter(k)}
                    style={{ padding:"6px 14px", borderRadius:20, fontSize:13, fontWeight:600, cursor:"pointer", border:"none",
                      background: reimbFilter===k ? accentColor : "#f3f4f6",
                      color: reimbFilter===k ? "white" : "#374151" }}>
                    {l}
                    {k !== "all" && (
                      <span style={{ marginLeft:6, fontSize:11 }}>
                        ({reimbursements.filter(r => r.reimbursement_status === k).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {reimbursements.filter(r => reimbFilter === "all" || r.reimbursement_status === reimbFilter).length === 0 ? (
                <div className="card"><div className="empty-state"><div style={{ fontSize:48 }}>💰</div><h3>No reimbursements</h3><p>When a settled trip has expenses exceeding the advance, the reimbursement is auto-submitted and appears here for processing.</p></div></div>
              ) : (
                <div style={{ display:"grid", gap:14 }}>
                  {reimbursements
                    .filter(r => reimbFilter === "all" || r.reimbursement_status === reimbFilter)
                    .map(r => {
                      const sc = {
                        submitted:         { color:"#92400e", bg:"#fef3c7" },
                        under_review:      { color:"#1d4ed8", bg:"#dbeafe" },
                        approved:          { color:"#065f46", bg:"#d1fae5" },
                        payment_initiated: { color:"#6d28d9", bg:"#ede9fe" },
                        paid:              { color:"#065f46", bg:"#bbf7d0" },
                      }[r.reimbursement_status] || { color:"#9ca3af", bg:"#f3f4f6" };

                      return (
                        <div key={r.id} className="card" style={{ padding:"18px 24px" }}>
                          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:14 }}>
                            {/* Left: employee + trip info */}
                            <div style={{ flex:1 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6, flexWrap:"wrap" }}>
                                <h3 style={{ fontSize:15, fontWeight:700, color:"#1f2937", margin:0 }}>{r.title}</h3>
                                <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:sc.color, background:sc.bg, border:`1px solid ${sc.color}33` }}>
                                  {r.reimbursement_status?.replace("_"," ").toUpperCase()}
                                </span>
                                {isSuperAdmin && r.company && (
                                  <span style={{ padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:700,
                                    background: COMPANY_COLORS[r.company]?.bg || "#f3f4f6",
                                    color: COMPANY_COLORS[r.company]?.color || "#374151" }}>
                                    {COMPANY_LABELS[r.company] || r.company}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize:13, color:"#6b7280", marginBottom:4 }}>
                                👤 <strong>{r.employee_name}</strong> · {r.employee_email}
                              </div>
                              <div style={{ fontSize:12, color:"#9ca3af" }}>
                                {r.destination && <span>📍 {r.destination} &nbsp;&bull;&nbsp; </span>}
                                📅 {new Date(r.start_date).toLocaleDateString("en-IN")}
                                {r.end_date && ` — ${new Date(r.end_date).toLocaleDateString("en-IN")}`}
                              </div>
                              {/* Bank details */}
                              {(r.bank_account || r.bank_name) && (
                                <div style={{ fontSize:12, color:"#374151", marginTop:6, padding:"6px 10px", background:"#f9fafb", borderRadius:6, display:"inline-block" }}>
                                  🏦 {r.bank_name || ""}{r.bank_account ? ` · ${r.bank_account}` : ""}
                                  {r.ifsc_code ? ` · IFSC: ${r.ifsc_code}` : ""}
                                </div>
                              )}
                              {r.reimbursement_submitted_at && (
                                <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>
                                  Submitted {new Date(r.reimbursement_submitted_at).toLocaleString("en-IN")}
                                </div>
                              )}
                            </div>

                            {/* Right: amounts + action */}
                            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:10 }}>
                              <div style={{ display:"flex", gap:20 }}>
                                <div style={{ textAlign:"center" }}>
                                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>SPENT</div>
                                  <div style={{ fontSize:18, fontWeight:800, color:"#E31E24" }}>₹{parseFloat(r.total_expenses||0).toLocaleString("en-IN")}</div>
                                </div>
                                <div style={{ textAlign:"center" }}>
                                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>REIMBURSABLE</div>
                                  <div style={{ fontSize:18, fontWeight:800, color:"#059669" }}>₹{parseFloat(r.reimbursement_amount||0).toLocaleString("en-IN")}</div>
                                </div>
                              </div>
                              {r.canApprove === false ? (
                                <div style={{ fontSize:12, color:"#92400e", background:"#fef3c7", borderRadius:8, padding:"6px 12px", fontWeight:600 }}>
                                  🔒 Awaiting designated approver
                                </div>
                              ) : (
                                <button className="btn btn-primary btn-sm" onClick={() => openReimbModal(r)}>
                                  ✏️ Update Status
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}

          {/* Update Status Modal */}
          {showReimbModal && reimbTrip && (
            <div className="modal-overlay" onClick={() => setShowReimbModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:480 }}>
                <div className="modal-header">
                  <h2 style={{ fontSize:17, fontWeight:700 }}>💰 Update Reimbursement Status</h2>
                  <button onClick={() => setShowReimbModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
                </div>
                <form onSubmit={handleUpdateReimb}>
                  <div className="modal-body">
                    <div style={{ padding:"12px 14px", background:"#f9fafb", borderRadius:8, marginBottom:16, fontSize:13, color:"#374151" }}>
                      <strong>{reimbTrip.employee_name}</strong> · {reimbTrip.title}<br />
                      <span style={{ color:"#059669", fontWeight:700 }}>₹{parseFloat(reimbTrip.reimbursement_amount||0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">New Status *</label>
                      <select className="form-control" required value={reimbModalStatus} onChange={e => setReimbModalStatus(e.target.value)}>
                        <option value="">— Select status —</option>
                        <option value="under_review">🔍 Under Review</option>
                        <option value="approved">✅ Approved</option>
                        <option value="payment_initiated">🏦 Payment Initiated</option>
                        <option value="paid">💰 Paid</option>
                      </select>
                    </div>
                    {(reimbModalStatus === "payment_initiated" || reimbModalStatus === "paid") && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Reimbursable Amount (₹)</label>
                          <input type="number" step="0.01" className="form-control"
                            value={reimbModalAmount} onChange={e => setReimbModalAmount(e.target.value)}
                            placeholder="Confirm or adjust amount" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Bank Reference / UTR</label>
                          <input className="form-control" value={reimbModalRef}
                            onChange={e => setReimbModalRef(e.target.value)} placeholder="e.g. IMPS123456789" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Payment Date</label>
                          <input type="date" className="form-control" value={reimbModalDate}
                            onChange={e => setReimbModalDate(e.target.value)} />
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <label className="form-label">Notes (optional)</label>
                      <textarea className="form-control" rows={2} value={reimbModalNotes}
                        onChange={e => setReimbModalNotes(e.target.value)}
                        placeholder="e.g. Processed via HDFC — batch payment" />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowReimbModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={!!updatingReimbId}>
                      {updatingReimbId ? "Saving..." : "✅ Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {tab !== "advance-requests" && tab !== "advance-returns" && tab !== "reimbursements" && loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Loading...</div>
      ) : tab !== "advance-requests" && tab !== "advance-returns" && tab !== "reimbursements" && filtered.length === 0 ? (
        <div className="card"><div className="empty-state"><div style={{ fontSize:48 }}>✈️</div><h3>No {tab === "all" ? "" : tab} trips</h3></div></div>
      ) : tab !== "advance-requests" && tab !== "advance-returns" && tab !== "reimbursements" && (
        <div style={{ display:"grid", gap:14 }}>
          {filtered.map(trip => {
            const balance = parseFloat(trip.advance_amount||0) - employeeSpentForTrip(trip) - parseFloat(trip.returned_amount||0);
            return (
              <div key={trip.id} className="card" style={{ padding:"20px 24px", cursor:"pointer" }} onClick={() => viewTrip(trip)}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4, flexWrap:"wrap" }}>
                      <h3 style={{ fontSize:16, fontWeight:700, color:"#1f2937", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"100%" }}>{trip.title}</h3>
                      <span className={`badge badge-${trip.status === "correction" ? "pending" : trip.status}`}
                        style={trip.status === "correction" ? { background:"#FEF3C7", color:"#92400e" } : {}}>
                        {trip.status === "correction" ? "⚠️ Correction" : trip.status}
                      </span>
                      {isSuperAdmin && trip.employee_company && (
                        <span style={{ padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:700,
                          background: COMPANY_COLORS[trip.employee_company]?.bg || "#f3f4f6",
                          color: COMPANY_COLORS[trip.employee_company]?.color || "#374151" }}>
                          {COMPANY_LABELS[trip.employee_company] || trip.employee_company}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:13, color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      👤 {trip.employee_name || "—"} &nbsp;•&nbsp;
                      {trip.destination && <span>📍 {trip.destination} &nbsp;•&nbsp;</span>}
                      📅 {new Date(trip.start_date).toLocaleDateString("en-IN")}
                      {trip.end_date && ` — ${new Date(trip.end_date).toLocaleDateString("en-IN")}`}
                    </div>
                  </div>

                  <div style={{ display:"flex", gap:20, alignItems:"center", flexWrap:"wrap" }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>ADVANCE</div>
                      <div style={{ fontSize:16, fontWeight:800, color:"#d97706" }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>SPENT</div>
                      <div style={{ fontSize:16, fontWeight:800, color:"#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>{balance>=0?"REFUND":"PAY MORE"}</div>
                      <div style={{ fontSize:16, fontWeight:800, color:balance>=0?"#059669":"#DC2626" }}>₹{Math.abs(balance).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {trip.status === "active" && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => openAdvance(trip, e)}>💰 Issue Advance</button>
                      )}
                      {trip.status === "submitted" && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => openSettle(trip, e)}>✅ Settle</button>
                      )}
                      {(trip.status === "active" || trip.status === "correction") && (
                        <button
                          className="btn btn-sm"
                          onClick={(e) => openReminderModal(trip, e)}
                          style={{ border:"1px solid #3B82F6", color:"#1D4ED8", background:"#EFF6FF", fontWeight:700 }}>
                          🔔 Remind
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); viewTrip(trip); }}>View Expenses →</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Send Reminder Modal (list view) ── */}
      {showReminderModal && reminderTrip && (
        <div className="modal-overlay" onClick={() => setShowReminderModal(false)}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:18, fontWeight:700 }}>🔔 Send Reminder</h2>
              <button onClick={() => setShowReminderModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <form onSubmit={handleSendReminder}>
              <div className="modal-body">
                <div style={{ padding:"14px 16px", background:"#EFF6FF", borderRadius:12, marginBottom:16, border:"1px solid #BFDBFE" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1e40af", marginBottom:4 }}>Sending reminder to:</div>
                  <div style={{ fontWeight:800, color:"#1f2937" }}>{reminderTrip.employee_name}</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>Trip: {reminderTrip.title}</div>
                  {parseFloat(reminderTrip.advance_amount||0) > 0 && (
                    <div style={{ fontSize:13, color:"#d97706", fontWeight:600, marginTop:4 }}>
                      Advance: ₹{parseFloat(reminderTrip.advance_amount).toLocaleString("en-IN")}
                    </div>
                  )}
                </div>
                <div style={{ padding:"12px 16px", background:"#F0F9FF", borderRadius:10, marginBottom:16, fontSize:13, color:"#0369a1", border:"1px solid #BAE6FD" }}>
                  <strong>What happens:</strong> Employee will receive an email + push notification reminding them to submit their expense bills.
                </div>
                <div className="form-group">
                  <label className="form-label">Custom Message (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="e.g. Please submit your bills at the earliest. Accounts closing this week."
                    value={reminderMessage}
                    onChange={e => setReminderMessage(e.target.value)}
                    style={{ resize:"vertical", minHeight:80 }}
                  />
                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Leave blank to send the standard reminder.</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowReminderModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={sendingReminder}
                  style={{ background:"#1D4ED8" }}>
                  {sendingReminder ? "Sending..." : "🔔 Send Reminder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Mark as Credited Modal ── */}
      {showCreditModal && creditReq && (
        <div className="modal-overlay" onClick={() => setShowCreditModal(false)}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:18, fontWeight:700 }}>💸 Mark Advance as Credited</h2>
              <button onClick={() => setShowCreditModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <form onSubmit={handleCredit}>
              <div className="modal-body">
                <div style={{ padding:"14px 16px", background:"#EFF6FF", borderRadius:12, marginBottom:18, border:"1px solid #BFDBFE" }}>
                  <div style={{ fontSize:13, color:"#1e40af", fontWeight:600, marginBottom:6 }}>Confirming credit for:</div>
                  <div style={{ fontWeight:800, color:"#1f2937", fontSize:16 }}>{creditReq.employee_name}</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>{creditReq.employee_email}</div>
                  <div style={{ fontSize:13, color:"#374151", marginTop:6 }}>Trip: <strong>{creditReq.trip_title}</strong></div>
                  <div style={{ fontSize:22, fontWeight:800, color:"#1D4ED8", marginTop:8 }}>
                    ₹{parseFloat(creditReq.approved_amount ?? creditReq.amount).toLocaleString("en-IN")}
                    {creditReq.approved_amount != null && parseFloat(creditReq.approved_amount) !== parseFloat(creditReq.amount) && (
                      <span style={{ fontSize:13, fontWeight:400, color:"#9ca3af", marginLeft:8 }}>
                        (requested: ₹{parseFloat(creditReq.amount).toLocaleString("en-IN")})
                      </span>
                    )}
                  </div>
                </div>

                {creditReq.request_type !== "supplementary" && Number(creditReq.company_owed_balance || 0) > 0 && (
                  <div style={{ padding:"14px 16px", background:"#ECFDF5", borderRadius:12, marginBottom:16, border:"1px solid #A7F3D0", color:"#065F46" }}>
                    <div style={{ fontSize:13, fontWeight:800, marginBottom:8 }}>Previous company liability is included in this bank transfer</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:"6px 12px", fontSize:13 }}>
                      <span>New trip's usable advance</span>
                      <strong>₹{Number(creditAmount || 0).toLocaleString("en-IN")}</strong>
                      <span>Previous excess owed to employee</span>
                      <strong>+ ₹{Number(creditReq.company_owed_balance || 0).toLocaleString("en-IN")}</strong>
                      <span style={{ borderTop:"1px solid #6EE7B7", paddingTop:6, fontWeight:800 }}>Total amount to transfer</span>
                      <strong style={{ borderTop:"1px solid #6EE7B7", paddingTop:6, fontSize:16 }}>
                        ₹{(Number(creditAmount || 0) + Number(creditReq.company_owed_balance || 0)).toLocaleString("en-IN")}
                      </strong>
                    </div>
                    <div style={{ fontSize:12, marginTop:8 }}>
                      The previous liability is paid in addition to this advance. It does not reduce the employee's usable trip budget.
                    </div>
                  </div>
                )}

                {creditReq.request_type !== "supplementary" && Number(creditReq.carry_forward_balance || 0) > 0 && (
                  <div style={{ padding:"14px 16px", background:"#FFFBEB", borderRadius:12, marginBottom:16, border:"1px solid #FCD34D", color:"#78350F" }}>
                    <div style={{ fontSize:13, fontWeight:800, marginBottom:8 }}>Carry-forward will be added to this advance (no extra transfer)</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:"6px 12px", fontSize:13 }}>
                      <span>Cash you transfer</span>
                      <strong>₹{Number(creditAmount || 0).toLocaleString("en-IN")}</strong>
                      <span>Employee's unspent carry-forward</span>
                      <strong>+ ₹{Number(creditReq.carry_forward_balance || 0).toLocaleString("en-IN")}</strong>
                      <span style={{ borderTop:"1px solid #FCD34D", paddingTop:6, fontWeight:800 }}>Employee can spend on this trip</span>
                      <strong style={{ borderTop:"1px solid #FCD34D", paddingTop:6, fontSize:16 }}>
                        ₹{(Number(creditAmount || 0) + Number(creditReq.carry_forward_balance || 0)).toLocaleString("en-IN")}
                      </strong>
                    </div>
                    <div style={{ fontSize:12, marginTop:8 }}>
                      The carry-forward is taken from the employee's balance when you confirm. If an earlier trip is still awaiting settlement, settle it first so any further balance is included.
                    </div>
                  </div>
                )}

                {/* Delay info */}
                <div style={{ background:"#f9fafb", borderRadius:10, padding:"12px 14px", marginBottom:16, fontSize:13, color:"#374151" }}>
                  <div>📅 Requested: <strong>{fmtDateTime(creditReq.requested_at)}</strong></div>
                  <div style={{ marginTop:4 }}>✅ Approved: <strong>{fmtDateTime(creditReq.approved_at)}</strong></div>
                  <div style={{ marginTop:4, color: creditReq.hours_pending > 48 ? "#DC2626" : "#059669", fontWeight:600 }}>
                    ⏱ Time since approval: {fmtHours(creditReq.hours_pending)}
                    {creditReq.hours_pending > 48 && " — overdue!"}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Amount Credited *</label>
                  <div style={{ fontSize:11, color:"#6b7280", margin:"-5px 0 6px" }}>
                    {creditReq.request_type === "supplementary"
                      ? "Additional usable advance for this same trip."
                      : "Usable advance for the new trip; any previous company liability is added separately below."}
                  </div>
                  <input type="number" className="form-control" min="0.01" step="0.01" required
                    value={creditAmount} onChange={e => setCreditAmount(e.target.value)} />
                  {creditAmount && Math.abs(parseFloat(creditAmount) - parseFloat(creditReq.approved_amount ?? creditReq.amount)) >= 0.005 && (
                    <div style={{ marginTop:6, fontSize:12, fontWeight:700, color: parseFloat(creditAmount) < parseFloat(creditReq.approved_amount ?? creditReq.amount) ? "#B45309" : "#1D4ED8" }}>
                      Difference from approved: {parseFloat(creditAmount) < parseFloat(creditReq.approved_amount ?? creditReq.amount) ? "−" : "+"}₹{Math.abs(parseFloat(creditAmount) - parseFloat(creditReq.approved_amount ?? creditReq.amount)).toLocaleString("en-IN")}
                    </div>
                  )}
                </div>

                {creditAmount && Math.abs(parseFloat(creditAmount) - parseFloat(creditReq.approved_amount ?? creditReq.amount)) >= 0.005 && (
                  <div className="form-group">
                    <label className="form-label">Reason for Amount Adjustment *</label>
                    <textarea className="form-control" rows={2} required
                      placeholder="Explain why Finance credited a different amount"
                      value={creditAdjustmentReason} onChange={e => setCreditAdjustmentReason(e.target.value)} />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Bank Transfer / UPI Reference</label>
                  <input type="text" className="form-control" placeholder="e.g. NEFT ref UTR12345678"
                    value={creditBankRef} onChange={e => setCreditBankRef(e.target.value)} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreditModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={!!creditingId}
                  style={{ background:"#1D4ED8" }}>
                  {creditingId ? "Saving..." : "💸 Confirm Credit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Request Bill Correction Modal ── */}
      {showCorrectionModal && correctionExp && (
        <div className="modal-overlay" onClick={() => setShowCorrectionModal(false)}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:18, fontWeight:700 }}>⚠️ Request Bill Correction</h2>
              <button onClick={() => setShowCorrectionModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <form onSubmit={handleRequestCorrection}>
              <div className="modal-body">
                {/* Expense summary */}
                <div style={{ padding:"14px 16px", background:"#FEF9F0", border:"1px solid #FDE68A", borderRadius:12, marginBottom:18 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#92400e", textTransform:"uppercase", marginBottom:6 }}>Flagging this expense</div>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:22 }}>{CATEGORY_ICONS[correctionExp.category]||"📌"}</span>
                    <div>
                      <div style={{ fontWeight:700, color:"#1f2937" }}>{correctionExp.category} — {correctionExp.description}</div>
                      <div style={{ fontSize:13, color:"#6b7280" }}>
                        ₹{parseFloat(correctionExp.amount).toLocaleString("en-IN")} •
                        📅 {new Date(correctionExp.expense_date).toLocaleDateString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding:"12px 16px", background:"#FFFBEB", borderRadius:10, marginBottom:16, fontSize:13, color:"#78350f", border:"1px solid #FDE68A" }}>
                  <strong>What happens:</strong> The trip status will change to "Correction" and the employee will receive an email notification with your note, asking them to reupload the correct bill.
                </div>

                <div className="form-group">
                  <label className="form-label">Correction Note for Employee *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="e.g. Bill is blurry, please reupload a clearer photo. Or: Bill amount doesn't match the claimed amount."
                    value={correctionNote}
                    onChange={e => setCorrectionNote(e.target.value)}
                    required
                    autoFocus
                    style={{ resize:"vertical", minHeight:80 }}
                  />
                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>This note will appear in the employee's email and on their trip page.</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCorrectionModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={requestingCorrection}
                  style={{ background:"#D97706" }}>
                  {requestingCorrection ? "Sending..." : "⚠️ Request Correction"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Issue Advance Modal ── */}
      {showAdvanceModal && advanceTrip && (
        <div className="modal-overlay" onClick={() => setShowAdvanceModal(false)}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:18, fontWeight:700 }}>💰 Issue Advance</h2>
              <button onClick={() => setShowAdvanceModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <form onSubmit={handleIssueAdvance}>
              <div className="modal-body">
                <div style={{ padding:"12px 16px", background:"#f9fafb", borderRadius:10, marginBottom:16 }}>
                  <div style={{ fontSize:13, color:"#6b7280" }}>Issuing advance for:</div>
                  <div style={{ fontWeight:700, color:"#1f2937", marginTop:4 }}>{advanceTrip.title}</div>
                  <div style={{ fontSize:13, color:"#9ca3af" }}>👤 {advanceTrip.employee_name}</div>
                  {advanceTrip.bank_account && (
                    <div style={{ fontSize:12, color:"#059669", marginTop:6, fontWeight:600 }}>
                      🏦 {advanceTrip.bank_name} · A/C: {advanceTrip.bank_account} · IFSC: {advanceTrip.ifsc_code}
                    </div>
                  )}
                  {parseFloat(advanceTrip.advance_amount||0) > 0 && (
                    <div style={{ fontSize:12, color:"#d97706", marginTop:6, fontWeight:600 }}>
                      Previously issued: ₹{parseFloat(advanceTrip.advance_amount).toLocaleString("en-IN")}
                    </div>
                  )}
                </div>
                {advanceCarryForward > 0 && (
                  <div style={{ padding:"12px 14px", background:"#D1FAE5", border:"1.5px solid #6EE7B7", borderRadius:10, marginBottom:14 }}>
                    <div style={{ fontWeight:800, color:"#065F46", fontSize:13 }}>💰 Carry-Forward Balance</div>
                    <div style={{ fontSize:13, color:"#047857", marginTop:4 }}>
                      This employee has <strong>₹{advanceCarryForward.toLocaleString("en-IN", { minimumFractionDigits:2 })}</strong> from an earlier trip.
                      You can keep it separate, or apply some/all of it to this trip below. It remains separately recorded in the audit trail.
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Advance Amount to Credit (₹) *</label>
                  <input type="number" className="form-control" placeholder="e.g. 15000" min="1" step="0.01"
                    value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} required autoFocus />
                  {advanceCarryForward > 0 && advanceAmount && parseFloat(advanceAmount) > 0 && (
                    <div style={{ marginTop:6, fontSize:12, color:"#047857", fontWeight:600 }}>
                      This cash top-up is added to the trip's previously issued advance; it will not replace it.
                    </div>
                  )}
                </div>
                {advanceCarryForward > 0 && (
                  <div className="form-group">
                    <label className="form-label">Apply Carry-Forward to This Trip (₹)</label>
                    <input type="number" className="form-control" placeholder="0.00" min="0" max={advanceCarryForward} step="0.01"
                      value={carryForwardToApply}
                      onChange={e => setCarryForwardToApply(e.target.value)} />
                    <div style={{ marginTop:6, fontSize:12, color:"#475569" }}>
                      Available carry-forward: ₹{advanceCarryForward.toLocaleString("en-IN", { minimumFractionDigits:2 })}. Applied carry-forward and cash credit become one bill/settlement balance for this trip.
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Bank Transfer / UPI Reference</label>
                  <input type="text" className="form-control" placeholder="e.g. NEFT/UPI ref number"
                    value={advanceBankRef} onChange={e => setAdvanceBankRef(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <input type="text" className="form-control" placeholder="Any additional notes"
                    value={advanceNotes} onChange={e => setAdvanceNotes(e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdvanceModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Issuing..." : "💰 Issue Advance"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Settle Trip Modal ── */}
      {showSettleModal && settleTrip && (() => {
        const bal = parseFloat(settleTrip.advance_amount||0) - employeeSpentForTrip(settleTrip) - parseFloat(settleTrip.returned_amount||0);
        return (
          <div className="modal-overlay" onClick={() => setShowSettleModal(false)}>
            <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize:18, fontWeight:700 }}>✅ Settle Trip</h2>
                <button onClick={() => setShowSettleModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
              </div>
              <form onSubmit={handleSettle}>
                <div className="modal-body">
                  <div style={{ padding:"14px 16px", background:"#f9fafb", borderRadius:10, marginBottom:16 }}>
                    <div style={{ fontWeight:700, color:"#1f2937" }}>{settleTrip.title}</div>
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:4 }}>👤 {settleTrip.employee_name}</div>
                    {settleTrip.bank_account && (
                      <div style={{ fontSize:12, color:"#059669", marginTop:6, fontWeight:600 }}>
                        🏦 {settleTrip.bank_name} · A/C: {settleTrip.bank_account} · IFSC: {settleTrip.ifsc_code}
                      </div>
                    )}
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(105px,1fr))", gap:10, marginBottom:16 }}>
                    {[
                      { label:"Advance Given",  value:`₹${parseFloat(settleTrip.advance_amount||0).toLocaleString("en-IN")}`, color:"#d97706", bg:"#FEF3C7" },
                      { label:"Total Spent",    value:`₹${parseFloat(settleTrip.total_expenses||0).toLocaleString("en-IN")}`, color:"#E31E24", bg:"#FEE2E2" },
                      { label:"Returned", value:`₹${parseFloat(settleTrip.returned_amount||0).toLocaleString("en-IN")}`, color:"#0369A1", bg:"#E0F2FE" },
                      { label:bal>=0?"Carry Forward":"Pay Extra", value:`₹${Math.abs(bal).toLocaleString("en-IN")}`, color:bal>=0?"#059669":"#DC2626", bg:bal>=0?"#D1FAE5":"#FEE2E2" },
                    ].map(s => (
                      <div key={s.label} style={{ background:s.bg, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:s.color, opacity:0.7, textTransform:"uppercase" }}>{s.label}</div>
                        <div style={{ fontSize:16, fontWeight:800, color:s.color, marginTop:4 }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {bal > 0 && (
                    <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#D1FAE5", border:"1px solid #6EE7B7" }}>
                      <div style={{ fontWeight:700, color:"#065F46", fontSize:14 }}>
                        ✅ Balance of ₹{Math.abs(bal).toLocaleString("en-IN")} kept in employee's account
                      </div>
                      <div style={{ fontSize:12, color:"#047857", marginTop:4 }}>
                        This surplus stays with the employee and will be deducted from their next advance request — no collection needed.
                      </div>
                    </div>
                  )}

                  {renderOverspendResolution(bal)}
                  {bal < 0 && settleOverspendResolution === "next_advance" && (
                    <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#EFF6FF", border:"1px solid #93C5FD" }}>
                      <div style={{ fontWeight:700, color:"#1E3A8A", fontSize:14 }}>↪ Carry ₹{Math.abs(bal).toLocaleString("en-IN")} into the next advance</div>
                      <div style={{ fontSize:12, color:"#1D4ED8", marginTop:4 }}>The current trip can close now. This amount will be added to the employee's next bank credit without reducing that trip's working advance.</div>
                    </div>
                  )}
                  {bal < 0 && settleOverspendResolution === "reimbursement" && (
                    <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#FEE2E2", border:"1px solid #FCA5A5" }}>
                      <div style={{ fontWeight:700, color:"#991b1b", fontSize:14 }}>
                        ➕ Pay ₹{Math.abs(bal).toLocaleString("en-IN")} to employee
                      </div>
                      <div style={{ fontSize:12, color:"#b91c1c", marginTop:4 }}>
                        Employee spent more than the advance. Payment details below are required.
                      </div>
                    </div>
                  )}

                  {bal === 0 && (
                    <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:"#F3F4F6", border:"1px solid #E5E7EB" }}>
                      <div style={{ fontWeight:700, color:"#374151" }}>✅ Perfectly balanced — no payment needed</div>
                    </div>
                  )}

                  {/* Payment details — required when expense > advance */}
                  {bal < 0 && settleOverspendResolution === "reimbursement" && (
                    <div style={{ border:"1px solid #FCA5A5", borderRadius:10, padding:"14px 16px", marginBottom:16, background:"#FFF5F5" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#991b1b", marginBottom:12 }}>💸 Payment Details (required)</div>
                      <div className="form-group" style={{ marginBottom:12 }}>
                        <label className="form-label">Payment Date *</label>
                        <input type="date" className="form-control" required
                          value={settlePaymentDate} onChange={e => setSettlePaymentDate(e.target.value)} />
                      </div>
                      <div className="form-group" style={{ marginBottom:12 }}>
                        <label className="form-label">Amount Paid (₹) *</label>
                        <input type="number" className="form-control" placeholder={Math.abs(bal).toFixed(2)} min="0" step="0.01" required
                          value={settlePaymentAmount} onChange={e => setSettlePaymentAmount(e.target.value)} />
                      </div>
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label">Bank Transfer / UPI Reference</label>
                        <input type="text" className="form-control" placeholder="e.g. UTR/UPI ref number"
                          value={settleBankTransferRef} onChange={e => setSettleBankTransferRef(e.target.value)} />
                      </div>
                    </div>
                  )}

                  {bal < 0 && settleOverspendResolution === "reimbursement" && (
                  <div className="form-group">
                    <label className="form-label">Bank / UPI Reference (payment to employee)</label>
                    <input className="form-control" placeholder="e.g. NEFT/UPI transaction ref"
                      value={settleBankRef} onChange={e => setSettleBankRef(e.target.value)} />
                  </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Settlement Notes (optional)</label>
                    <input className="form-control" placeholder="e.g. Settled on 01-May-2026"
                      value={settleNote} onChange={e => setSettleNote(e.target.value)} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowSettleModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving..." : settleOverspendResolution === "supplementary_advance"
                      ? "Request Additional Advance" : "✅ Mark as Settled"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
